import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLES = [
  ["directivo_con_grupo", "Personal directivo con grupo"],
  ["directivo_sin_grupo", "Personal directivo sin grupo"],
  ["subdirector_gestion", "Subdirector de gestión escolar"],
  ["subdirector_academico", "Subdirector académico"],
  ["docente", "Personal docente"],
  ["docente_educ_fisica", "Profesores de educación física"],
  ["docente_artes", "Profesores de actividades artísticas"],
  ["docente_idiomas", "Profesores de idiomas"],
  ["administrativo", "Personal administrativo, auxiliar y de servicios"],
];

const empty = () => Object.fromEntries(ROLES.map(([r]) => [r, { H: 0, M: 0 }]));

export function PersonalDialog({ plantel, open, onOpenChange, onSaved }) {
  const [values, setValues] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plantel && open) {
      const p = plantel.personal || {};
      const merged = empty();
      for (const [r] of ROLES) {
        merged[r] = { H: p[r]?.H || 0, M: p[r]?.M || 0 };
      }
      setValues(merged);
    }
  }, [plantel, open]);

  const update = (role, gen, v) => {
    const n = parseInt(v, 10);
    setValues((prev) => ({
      ...prev,
      [role]: { ...prev[role], [gen]: Number.isFinite(n) && n >= 0 ? n : 0 },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/planteles/${plantel.id}/personal`, values);
      toast.success("Personal actualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const totalH = ROLES.reduce((s, [r]) => s + (values[r]?.H || 0), 0);
  const totalM = ROLES.reduce((s, [r]) => s + (values[r]?.M || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Personal por función — {plantel?.nombre}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#545454] mb-2">
          Registre el número de personas por función y sexo. Se utiliza en el reporte oficial 911.
        </div>
        <div className="border border-[#E5E7EB] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-2">Función</th>
                <th className="px-3 py-2 w-24">Hombres</th>
                <th className="px-3 py-2 w-24">Mujeres</th>
                <th className="px-3 py-2 w-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map(([role, label]) => {
                const v = values[role];
                return (
                  <tr key={role} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-1.5">{label}</td>
                    <td className="px-2 py-1">
                      <Input
                        type="number" min="0"
                        data-testid={`personal-${role}-H`}
                        value={v.H}
                        onChange={(e) => update(role, "H", e.target.value)}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        type="number" min="0"
                        data-testid={`personal-${role}-M`}
                        value={v.M}
                        onChange={(e) => update(role, "M", e.target.value)}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="px-3 py-1.5 font-semibold text-center">{(v.H || 0) + (v.M || 0)}</td>
                  </tr>
                );
              })}
              <tr className="bg-[#235B4E]/5 font-semibold border-t border-[#235B4E]/20">
                <td className="px-3 py-2 text-[#10312B]">TOTAL</td>
                <td className="px-3 py-2 text-center">{totalH}</td>
                <td className="px-3 py-2 text-center">{totalM}</td>
                <td className="px-3 py-2 text-center">{totalH + totalM}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="btn-save-personal" onClick={save} disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
            {saving ? "Guardando…" : "Guardar personal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
